package com.dotdrives.web.pages.updates

import com.dotdrives.controller.GivingSummaryController
import com.dotdrives.controller.GivingSummaryUiState
import com.dotdrives.design.DotAvatarPalette
import com.dotdrives.model.StewardGivingSummary
import com.dotdrives.model.TargetGivingSummary
import com.dotdrives.model.ReportPeriodKind
import com.dotdrives.model.customReportPeriod
import com.dotdrives.model.rollingTwelveMonths
import com.dotdrives.model.goalProgress
import com.dotdrives.model.goalWindowProgress
import com.dotdrives.model.ReportFilter
import com.dotdrives.model.fiscalYearPeriod
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.minus
import kotlinx.datetime.LocalDate
import com.dotdrives.web.ui.DotAction
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotEmptyState
import com.dotdrives.web.ui.DotSkeleton
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.style
import react.dom.html.ReactHTML.table
import react.dom.html.ReactHTML.caption
import react.dom.html.ReactHTML.tbody
import react.dom.html.ReactHTML.tr
import react.dom.html.ReactHTML.td
import react.dom.html.ReactHTML.details
import react.dom.html.ReactHTML.summary as summaryElement
import react.dom.svg.ReactSVG.svg
import react.dom.svg.ReactSVG.polyline
import react.useState
import react.useEffect
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType

external interface ReportsPageProps : Props {
    var currentFiscalYear: Int
    var controller: GivingSummaryController
    var state: GivingSummaryUiState
    var availableTargets: List<TargetGivingSummary>
    var availableStewards: List<StewardGivingSummary>
}

val ReportsPage = FC<ReportsPageProps> { props ->
    val state = props.state
    val summary = state.summary
    val today = LocalDate.parse(kotlin.js.Date().toISOString().take(10))
    val (customStart,setCustomStart)=useState(state.period?.start?.toString() ?: today.toString())
    val (customEnd,setCustomEnd)=useState(state.period?.endInclusive?.toString() ?: today.toString())
    val (customError,setCustomError)=useState<String?>(null)
    useEffect(state.period,state.periodKind) { if(state.periodKind==ReportPeriodKind.Custom) state.period?.let { setCustomStart(it.start.toString());setCustomEnd(it.endInclusive.toString()) } }
    useEffect(state.year,state.periodKind,state.targetId,state.stewardUserId,state.period) {
        val p=state.period
        val url=reportUrl(ReportUrlState(state.year,state.periodKind.name.lowercase(),p?.start?.toString(),p?.endInclusive?.toString(),state.targetId,state.stewardUserId))
        kotlinx.browser.window.history.replaceState(null,"",com.dotdrives.web.workspaceHash(url))
    }

    div {
        className = ClassName("reports-page")
        style { +reportsResponsiveCss() }
        div {
            className = ClassName("reports-header")
            div { className = ClassName("reports-title"); +"Reports" }
            div {
                className = ClassName("reports-filters")
                select {
                    className=ClassName("reports-filter-select"); ariaLabel="Period"
                    value=state.periodKind.name
                    onChange={ when(it.currentTarget.value) { "RollingTwelve" -> props.controller.setPeriod(ReportPeriodKind.RollingTwelve,rollingTwelveMonths(today)); "Custom" -> props.controller.setPeriod(ReportPeriodKind.Custom,state.period ?: customReportPeriod(today,today)); else -> props.controller.setYear(state.year) } }
                    option { value="FiscalYear"; +"Fiscal year" }; option { value="RollingTwelve"; +"Last 12 months" }; option { value="Custom"; +"Custom" }
                }
                select {
                    className = ClassName("reports-filter-select")
                    ariaLabel = "Year"
                    value = state.year.toString()
                    onChange = { props.controller.setYear(it.currentTarget.value.toInt()) }
                    reportYearOptions(props.currentFiscalYear, state.year).forEach { year ->
                        option { key = year.toString().unsafeCast<Key>(); value = year.toString(); +"Year $year" }
                    }
                }
                select {
                    className=ClassName("reports-filter-select"); ariaLabel="Steward"; value=state.stewardUserId?:""
                    onChange={props.controller.setSteward(it.currentTarget.value.ifBlank { null })}
                    option { value=""; +"All stewards" }
                    props.availableStewards.forEach { s -> option { key=s.userId.unsafeCast<Key>(); value=s.userId; +s.name } }
                }
                if(state.periodKind==ReportPeriodKind.Custom) {
                    input { className=ClassName("reports-filter-select"); ariaLabel="Custom start"; type="date".unsafeCast<web.html.InputType>(); value=customStart; onChange={setCustomStart(it.currentTarget.value)} }
                    input { className=ClassName("reports-filter-select"); ariaLabel="Custom end"; type="date".unsafeCast<web.html.InputType>(); value=customEnd; onChange={setCustomEnd(it.currentTarget.value)} }
                    button { type=ButtonType.buttonType; className=ClassName("reports-export-button"); onClick={ val p=runCatching { customReportPeriod(LocalDate.parse(customStart),LocalDate.parse(customEnd)) }.getOrNull(); if(p==null)setCustomError("Choose a valid start date on or before the end date.") else { setCustomError(null);props.controller.setPeriod(ReportPeriodKind.Custom,p) } }; +"Apply dates" }
                }
                select {
                    className = ClassName("reports-filter-select")
                    ariaLabel = "Target"
                    value = state.targetId ?: ""
                    onChange = { props.controller.setTarget(it.currentTarget.value.ifBlank { null }) }
                    option { value = ""; +"All targets" }
                    props.availableTargets.forEach { target ->
                        option { key = target.targetId.unsafeCast<Key>(); value = target.targetId; +target.name }
                    }
                }
                button {
                    type = ButtonType.buttonType
                    className = ClassName("reports-export-button")
                    disabled = summary == null || summary.months.isEmpty()
                    onClick = {
                        summary?.let { downloadCsv(buildMonthlySummaryCsv(it.months), "dot-drives-monthly-summary.csv") }
                    }
                    +"⇩ Export CSV"
                }
            }
        }
        customError?.let { div { className=ClassName("reports-filter-error"); role="alert".unsafeCast<react.dom.aria.AriaRole>(); +it } }

        state.error?.let { error ->
            DotBanner { message = error; onRetry = props.controller::retry; className = "updates-inline-banner" }
        }

        when {
            state.restricted -> ReportsRestricted()
            state.loading && summary == null && state.error == null -> ReportsSkeleton()
            summary != null -> {
                val content=reportsContentState(summary)
                if(content.showEmptyNotice) ReportsEmpty { isFiltered=summary.hasAnyGifts;onClearFilters={props.controller.setFilter(ReportFilter(state.period?:fiscalYearPeriod(state.year,1,1),state.periodKind),state.year)} }
                if(content.showReportBody) ReportsBody { this.summary=summary }
            }
        }
    }
}

private external interface ReportsBodyProps : Props {
    var summary: com.dotdrives.model.GivingSummary
}

internal data class ReportCumulativeRow(val label:String,val actual:Double,val prior:Double,val goal:Double?)
internal fun reportCumulativeRows(summary:com.dotdrives.model.GivingSummary):List<ReportCumulativeRow> {
    var actual=0.0;var prior=0.0
    val cutoff=summary.asOf?:summary.period?.endInclusive
    return summary.months.sortedWith(compareBy({it.year},{it.month})).filter { cutoff==null || it.year<cutoff.year || (it.year==cutoff.year && it.month<=cutoff.month.ordinal+1) }.map { month ->
        actual+=month.totalAmount; prior+=summary.priorMonths.firstOrNull { it.year==month.year&&it.month==month.month }?.totalAmount?:0.0
        val next=if(month.month==12) LocalDate(month.year+1,1,1) else LocalDate(month.year,month.month+1,1)
        val monthEnd=minOf(next.minus(DatePeriod(days=1)),cutoff?:next)
        ReportCumulativeRow(month.label,actual,prior,summary.period?.let { goalWindowProgress(actual,summary.orgBudget.goalAmount,summary.orgBudget.goalStart,summary.orgBudget.goalEnd,it.start,monthEnd).paceAmount })
    }
}

private val ReportsBody = FC<ReportsBodyProps> { props ->
    val summary = props.summary
    val pace=summary.period?.let { goalWindowProgress(summary.orgBudget.raisedAmount,summary.orgBudget.goalAmount,summary.orgBudget.goalStart,summary.orgBudget.goalEnd,it.start,summary.asOf?:it.endInclusive) }?:goalProgress(summary.orgBudget.raisedAmount,summary.orgBudget.goalAmount,summary.orgBudget.goalStart,summary.orgBudget.goalEnd,summary.asOf?:LocalDate(1970,1,1))
    val paceDifference=pace.paceDifference
    div {
        className = ClassName("reports-kpis")
        div { className=ClassName("reports-budget-card"); div { className=ClassName("reports-budget-label"); +"RAISED · ${summary.period?.label ?: "REPORT PERIOD"}" }; div { className=ClassName("reports-budget-amount"); +formatMoneyCompact(summary.orgBudget.raisedAmount); summary.orgBudget.goalAmount?.let { span { className=ClassName("reports-budget-goal"); +" of ${formatMoneyCompact(it)}" } } } }
        div { className=ClassName("reports-card reports-kpi"); div { className=ClassName("reports-budget-label"); +"VS SAME DATE LAST YEAR" }; div { className=ClassName("reports-kpi-value"); +formatMoneyCompact(summary.orgBudget.raisedAmount-summary.priorRaisedAmount) }; div { +"${formatMoneyCompact(summary.priorRaisedAmount)} prior period" } }
        div { className=ClassName("reports-card reports-kpi"); div { className=ClassName("reports-budget-label"); +"GIFTS" }; div { className=ClassName("reports-kpi-value"); +summary.giftCount.toString() }; div { val avg=if(summary.giftCount>0)summary.orgBudget.raisedAmount/summary.giftCount else 0.0; +"avg ${formatMoneyCompact(avg)} · recurring ${if(summary.orgBudget.raisedAmount>0) (summary.recurringAmount/summary.orgBudget.raisedAmount*100).toInt() else 0}%" } }
        div { className=ClassName("reports-card reports-kpi"); div { className=ClassName("reports-budget-label"); +"PACE" }; div { className=ClassName("reports-kpi-value"); +(paceDifference?.let { (if(it>=0) "+" else "")+formatMoneyCompact(it) }?:"—") }; div { +(if(paceDifference==null) "No dated organization goal" else if(paceDifference>=0) "ahead of pace" else "behind pace") } }
    }
    val rows=reportCumulativeRows(summary)
    val lines=reportChartLines(rows.map{it.actual},rows.map{it.prior},rows.mapNotNull{it.goal})
    div { className=ClassName("reports-card reports-chart"); div { className=ClassName("reports-card-title"); +"Cumulative giving vs. goal pace" }; div { +"Monthly cumulative actual, prior-year comparison, and goal pace" }; div { className=ClassName("reports-chart-legend"); span { className=ClassName("actual");+"Actual" };span { className=ClassName("prior");+"Prior year" };span { className=ClassName("goal");+"Goal pace" } }; svg { className=ClassName("reports-chart-svg"); role="img".unsafeCast<react.dom.aria.AriaRole>(); ariaLabel="Cumulative actual, prior year, and goal pace chart"; asDynamic().viewBox="0 0 800 220"; asDynamic().preserveAspectRatio="none"; polyline { asDynamic().points=lines.prior; asDynamic().fill="none";asDynamic().stroke="#B4BCC2";asDynamic().strokeWidth="3" }; polyline { asDynamic().points=lines.goal;asDynamic().fill="none";asDynamic().stroke="#88949C";asDynamic().strokeWidth="2";asDynamic().strokeDasharray="6 6" }; polyline { asDynamic().points=lines.actual;asDynamic().fill="none";asDynamic().stroke="#F26223";asDynamic().strokeWidth="4" } }; div { className=ClassName("reports-chart-axis"); rows.forEach { span { +it.label.take(3) } } }; details { className=ClassName("reports-chart-details"); summaryElement { +"View chart data" }; table { className=ClassName("reports-chart-data"); caption { +"Accessible cumulative giving data" }; tbody { rows.forEach { row -> tr { td { +row.label }; td { +"Actual ${formatMoneyCompact(row.actual)}" }; td { +"Prior ${formatMoneyCompact(row.prior)}" }; td { +"Goal ${row.goal?.let(::formatMoneyCompact) ?: "—"}" } } } } } } }
    div {
        className = ClassName("reports-grid")
        div {
            className = ClassName("reports-card")
            div { className = ClassName("reports-card-title"); +"Giving by target" }
            div {
                className = ClassName("reports-target-bars")
                if (summary.targets.isEmpty()) {
                    div { +"No targets configured yet." }
                } else {
                    summary.targets.forEach { target -> TargetBar { key = target.targetId.unsafeCast<Key>(); this.target = target } }
                }
            }
        }
        div {
            className = ClassName("reports-side-stack")
            div {
                className = ClassName("reports-budget-card")
                div { className = ClassName("reports-budget-label"); +"ORG BUDGET" }
                div {
                    className = ClassName("reports-budget-amount")
                    +formatMoneyCompact(summary.orgBudget.raisedAmount)
                    summary.orgBudget.goalAmount?.let { span { className = ClassName("reports-budget-goal"); +" of ${formatMoneyCompact(it)}" } }
                }
                div {
                    className = ClassName("reports-budget-track")
                    val fillStyle = js("({})")
                    fillStyle.width = "${targetProgressPercent(summary.orgBudget.raisedAmount, summary.orgBudget.goalAmount)}%"
                    div { className = ClassName("reports-budget-fill"); style = fillStyle }
                }
            }
            div {
                className = ClassName("reports-leaderboard-card")
                div { className = ClassName("reports-leaderboard-label"); +"STEWARD LEADERBOARD" }
                if (summary.stewards.isEmpty()) {
                    div { +"No gifts recorded yet." }
                } else {
                    summary.stewards.take(5).forEach { steward ->
                        LeaderboardRow { key = steward.userId.unsafeCast<Key>(); this.steward = steward }
                    }
                }
            }
        }
    }
    div {
        className = ClassName("reports-card")
        div { className = ClassName("reports-card-title"); +"Monthly summary" }
        if (summary.months.isEmpty()) {
            div { +"No gifts recorded in this window." }
        } else {
            div {
                className = ClassName("reports-monthly-scroll")
              div {
                className = ClassName("reports-monthly-table")
                div { className = ClassName("reports-monthly-cell reports-monthly-head"); +"MONTH" }
                div { className = ClassName("reports-monthly-cell reports-monthly-head"); +"GIFTS" }
                div { className = ClassName("reports-monthly-cell reports-monthly-head"); +"ONLINE" }
                div { className = ClassName("reports-monthly-cell reports-monthly-head"); +"MANUAL" }
                div { className = ClassName("reports-monthly-cell reports-monthly-head"); +"RECURRING" }
                div { className = ClassName("reports-monthly-cell reports-monthly-head"); +"TOTAL" }
                summary.months.forEach { month ->
                    div { className = ClassName("reports-monthly-cell reports-monthly-month"); +month.label }
                    div { className = ClassName("reports-monthly-cell"); +month.giftCount.toString() }
                    div { className = ClassName("reports-monthly-cell"); +formatMoneyCompact(month.onlineAmount) }
                    div { className = ClassName("reports-monthly-cell"); +formatMoneyCompact(month.manualAmount) }
                    div { className = ClassName("reports-monthly-cell reports-monthly-recurring"); +formatMoneyCompact(month.recurringAmount) }
                    div { className = ClassName("reports-monthly-cell reports-monthly-total"); +formatMoneyCompact(month.totalAmount) }
                }
              }
            }
        }
    }
}

private external interface TargetBarProps : Props {
    var target: TargetGivingSummary
}

private val TargetBar = FC<TargetBarProps> { props ->
    val target = props.target
    val (bg, _) = DotAvatarPalette.tintFor(target.targetId)
    val fillStyle = js("({})")
    fillStyle.width = "${targetProgressPercent(target.raisedAmount, target.goalAmount)}%"
    fillStyle.backgroundColor = bg
    div {
        div {
            className = ClassName("reports-target-row-head")
            span { className = ClassName("reports-target-name"); +target.name }
            span {
                className = ClassName("reports-target-amt")
                +formatMoneyCompact(target.raisedAmount)
                target.goalAmount?.let { goal ->
                    span { className = ClassName("reports-target-amt-goal"); +" / ${formatMoneyCompact(goal)}" }
                }
            }
        }
        div {
            className = ClassName("reports-bar-track")
            div { className = ClassName("reports-bar-fill"); style = fillStyle }
        }
    }
}

private external interface LeaderboardRowProps : Props {
    var steward: StewardGivingSummary
}

/**
 * The percentage here is budget attainment (`budgets` where `scope='steward'`), not share of
 * the leaderboard total -- the design's own numbers rule out share-of-total (SJ $84.2K/70%,
 * MR $71.5K/65%, DL $52.8K/88%: the lowest earner has the highest percentage, which
 * share-of-total, monotonic in raisedAmount, can never reproduce). No budget row for this
 * steward renders "--", not 0%.
 */
private val LeaderboardRow = FC<LeaderboardRowProps> { props ->
    val steward = props.steward
    val (bg, _) = DotAvatarPalette.tintFor(steward.userId)
    val avatarStyle = js("({})")
    avatarStyle.backgroundColor = bg
    div {
        className = ClassName("reports-leaderboard-row")
        div { className = ClassName("reports-leaderboard-avatar"); style = avatarStyle; +updateInitials(steward.name) }
        span { className = ClassName("reports-leaderboard-name"); +steward.name }
        span { className = ClassName("reports-leaderboard-amt"); +formatMoneyCompact(steward.raisedAmount) }
        span {
            +if (steward.budgetAmount == null) "—" else "${targetProgressPercent(steward.raisedAmount, steward.budgetAmount)}%"
        }
    }
}

/** `donations` RLS (`has_perm_strict`) excludes `field` entirely -- [GivingSummaryController]
 *  never issues the query for one, so this state is reached by role, not by an empty result. */
private val ReportsRestricted = FC<Props> {
    div {
        className = ClassName("reports-restricted-wrap")
        DotEmptyState {
            emoji = "🔒"
            title = "Reports are a staff view"
            body = "Giving totals aren't shown to field reps. Ask an admin or staff member for a summary."
            primaryAction = null
        }
    }
}

private external interface ReportsEmptyProps : Props {
    var isFiltered: Boolean
    var onClearFilters: () -> Unit
}

private val ReportsEmpty = FC<ReportsEmptyProps> { props ->
    div {
        className = ClassName("reports-empty-wrap")
        if (props.isFiltered) {
            DotEmptyState {
                emoji = "🔍"
                title = "No gifts for this target"
                body = "Try a different target, or clear the filter to see every target."
                primaryAction = DotAction("Clear target filter", props.onClearFilters)
            }
        } else {
            DotEmptyState {
                emoji = "📊"
                title = "No gifts recorded yet"
                body = "Once donations start coming in, giving-by-target, the org budget, and the steward leaderboard will show up here."
                primaryAction = null
            }
        }
    }
}

private val ReportsSkeleton = FC<Props> {
    div {
        className = ClassName("reports-skeleton-wrap")
        div {
            className = ClassName("reports-grid")
            DotSkeleton { height = "220px" }
            DotSkeleton { height = "220px" }
        }
        DotSkeleton { height = "180px" }
    }
}

private fun downloadCsv(csv: String, fileName: String) {
    val holder = js("({})")
    holder.csv = csv
    holder.fileName = fileName
    kotlinx.browser.window.asDynamic().__dotDrivesReportsCsvExport = holder
    js(
        """{
          const h = window.__dotDrivesReportsCsvExport;
          const blob = new Blob([h.csv], { type: 'text/csv;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = h.fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(url);
          delete window.__dotDrivesReportsCsvExport;
        }""",
    )
}
